import { useEffect, useRef, useState } from "react";
import "./editor.css";

const BUTTON_STYLE =
  "display:inline-block;padding:12px 22px;background:#0a2540;color:#ffffff;text-decoration:none;border-radius:6px;font-weight:bold;";

const escapeAttr = (s = "") =>
  String(s).replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");

const isSafeUrl = (url) => /^(https?:|mailto:)/i.test(String(url || "").trim());

/**
 * Small WYSIWYG editor for email bodies, built on contentEditable so we don't
 * need an editor dependency. Emits HTML through onChange.
 */
const RichTextEditor = ({ value, onChange, placeholder, disabled }) => {
  const ref = useRef(null);
  const [showHtml, setShowHtml] = useState(false);

  // only push values the DOM doesn't already have (loaded draft, HTML mode
  // edits), so typing doesn't reset the caret
  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== (value || "")) {
      ref.current.innerHTML = value || "";
    }
  }, [value, showHtml]);

  const emit = () => onChange(ref.current?.innerHTML || "");

  const exec = (command, arg) => {
    ref.current?.focus();
    document.execCommand(command, false, arg);
    emit();
  };

  const insertLink = () => {
    const url = window.prompt("Link URL (https://...)");
    if (!url) return;
    if (!isSafeUrl(url)) return window.alert("Links must start with https://, http:// or mailto:");
    exec("createLink", url.trim());
  };

  const insertButton = () => {
    const text = window.prompt("Button text");
    if (!text) return;
    const url = window.prompt("Button link (https://...)");
    if (!url) return;
    if (!isSafeUrl(url)) return window.alert("Links must start with https://, http:// or mailto:");
    exec(
      "insertHTML",
      `<p><a href="${escapeAttr(url.trim())}" style="${BUTTON_STYLE}">${escapeAttr(text)}</a></p><p></p>`,
    );
  };

  const insertImage = () => {
    const url = window.prompt("Image URL (https://...)");
    if (!url) return;
    if (!/^https:/i.test(url.trim())) return window.alert("Image URL must start with https://");
    exec(
      "insertHTML",
      `<img src="${escapeAttr(url.trim())}" alt="" style="max-width:100%;height:auto;display:block;margin:0 0 12px;" />`,
    );
  };

  const tools = [
    { label: <b>B</b>, title: "Bold", run: () => exec("bold") },
    { label: <i>I</i>, title: "Italic", run: () => exec("italic") },
    { label: <u>U</u>, title: "Underline", run: () => exec("underline") },
    { label: "H1", title: "Heading", run: () => exec("formatBlock", "<h2>") },
    { label: "H2", title: "Subheading", run: () => exec("formatBlock", "<h3>") },
    { label: "¶", title: "Paragraph", run: () => exec("formatBlock", "<p>") },
    { label: "• List", title: "Bulleted list", run: () => exec("insertUnorderedList") },
    { label: "1. List", title: "Numbered list", run: () => exec("insertOrderedList") },
    { label: "Link", title: "Insert link", run: insertLink },
    { label: "Button", title: "Insert button", run: insertButton },
    { label: "Image", title: "Insert image", run: insertImage },
    {
      label: "First name",
      title: "Insert the recipient's first name",
      run: () => exec("insertText", "{{first_name}}"),
    },
    { label: "Clear", title: "Clear formatting", run: () => exec("removeFormat") },
  ];

  return (
    <div className="border-[#E6E7E9] border-[1px] rounded-lg overflow-hidden">
      <div className="flex flex-wrap gap-1 bg-[#F7F8FA] border-b border-[#E6E7E9] p-2">
        {tools.map((t) => (
          <button
            key={t.title}
            type="button"
            title={t.title}
            disabled={disabled || showHtml}
            // keep the text selection when clicking the toolbar
            onMouseDown={(e) => e.preventDefault()}
            onClick={t.run}
            className="px-2 py-1 text-[13px] rounded hover:bg-white disabled:opacity-40"
          >
            {t.label}
          </button>
        ))}
        <button
          type="button"
          disabled={disabled}
          onClick={() => setShowHtml((v) => !v)}
          className="ml-auto px-2 py-1 text-[13px] rounded hover:bg-white"
        >
          {showHtml ? "Visual" : "HTML"}
        </button>
      </div>
      {showHtml ? (
        <textarea
          className="w-full min-h-[320px] p-4 font-mono text-[12px] outline-none resize-y"
          value={value || ""}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : (
        <div
          ref={ref}
          className="campaign-editor min-h-[320px] p-4 text-[15px] leading-6 outline-none"
          contentEditable={!disabled}
          suppressContentEditableWarning
          data-placeholder={placeholder}
          onInput={emit}
          onBlur={emit}
        />
      )}
    </div>
  );
};

export default RichTextEditor;
